/**
 * ScheduleService
 *
 * This service handles all schedule/Gantt view related operations.
 * It provides methods for retrieving schedule aggregates with filtering and pagination.
 *
 * @class
 */

import { pathWorkflowScheduleAggregates } from '@frontend/__settings__/septSoftwarePaths';
import HttpService from './HttpService';
import type {
  ScheduleAggregateRequestParams,
  ScheduleAggregateResponseData,
  WorkflowGenericResponse,
} from '@frontend/types/workflows';

const http = new HttpService();

class ScheduleService {
  /**
   * Retrieves aggregated schedule summaries for projects with optional filters and pagination.
   *
   * @param {ScheduleAggregateRequestParams} params - Optional filter and pagination configuration
   * @returns {Promise<WorkflowGenericResponse<ScheduleAggregateResponseData>>} Aggregated schedule data
   * @throws {Error} If the API request fails
   */
  static async getAggregates(
    params?: ScheduleAggregateRequestParams
  ): Promise<WorkflowGenericResponse<ScheduleAggregateResponseData>> {
    const queryParams = buildScheduleQuery(params);
    
    const response = await http.get<
      WorkflowGenericResponse<ScheduleAggregateResponseData>
    >(pathWorkflowScheduleAggregates, {
      params: queryParams,
    });

    if (!response.successful) {
      throw new Error(response.error || 'Failed to fetch schedule aggregates');
    }

    return response;
  }
}

export default ScheduleService;

/**
 * Builds query parameters for schedule aggregates API
 */
const buildScheduleQuery = (
  params?: ScheduleAggregateRequestParams
): Record<string, string | number> | undefined => {
  if (!params) {
    return undefined;
  }

  const query: Record<string, string | number> = {};
  let hasEntries = false;

  const filters = params.filters;
  if (filters) {
    const serializeList = (values?: string[] | null) =>
      values && values.length ? values.join(',') : undefined;

    const statusValue = serializeList(filters.statuses);
    if (statusValue) {
      query.status = statusValue;
      hasEntries = true;
    }

    const disciplineValue = serializeList(filters.disciplines);
    if (disciplineValue) {
      query.discipline = disciplineValue;
      hasEntries = true;
    }

    const clientValue = serializeList(filters.clientIds);
    if (clientValue) {
      query.clientId = clientValue;
      hasEntries = true;
    }

    const projectTypeValue = serializeList(filters.projectTypes);
    if (projectTypeValue) {
      query.projectType = projectTypeValue;
      hasEntries = true;
    }

    const userValue = serializeList(filters.userIds);
    if (userValue) {
      query.employeeId = userValue;
      hasEntries = true;
    }

    if (filters.startDate) {
      query.startDate = filters.startDate;
      hasEntries = true;
    }

    if (filters.endDate) {
      query.endDate = filters.endDate;
      hasEntries = true;
    }

    if (filters.search) {
      query.search = filters.search;
      hasEntries = true;
    }

    if (filters.projectStatus) {
      query.projectStatus = filters.projectStatus;
      hasEntries = true;
    }

    if (filters.clientName) {
      query.clientName = filters.clientName;
      hasEntries = true;
    }

    if (filters.ownerName) {
      query.ownerName = filters.ownerName;
      hasEntries = true;
    }

    if (filters.country) {
      query.country = filters.country;
      hasEntries = true;
    }

    if (filters.archived) {
      query.archived = filters.archived;
      hasEntries = true;
    }

    if (filters.openDateFrom) {
      query.openDateFrom = filters.openDateFrom;
      hasEntries = true;
    }

    if (filters.openDateTo) {
      query.openDateTo = filters.openDateTo;
      hasEntries = true;
    }
  }

  const pagination = params.pagination;
  if (pagination) {
    if (pagination.page != null) {
      query.page = pagination.page;
      hasEntries = true;
    }
    if (pagination.limit != null) {
      query.limit = pagination.limit;
      hasEntries = true;
    }
    if (pagination.sortBy) {
      query.sortBy = pagination.sortBy;
      hasEntries = true;
    }
    if (pagination.sortOrder) {
      query.sortOrder = pagination.sortOrder;
      hasEntries = true;
    }
  }

  return hasEntries ? query : undefined;
};

